import { FC, useEffect } from 'react';

import Box from '@material-ui/core/Box';
import Collapse from '@material-ui/core/Collapse';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { Controller, Control, useWatch } from 'react-hook-form';
import type { UseFormGetValues, UseFormSetValue, UseFormTrigger } from 'react-hook-form';

import FormCheckboxInput from 'components/_forms/FormCheckboxInput/FormCheckboxInput';
import FormSelectInput from 'components/_forms/FormSelectInput/FormSelectInput';
import { ON_GO_ACCESS } from 'constants/onGoAccess.config';

import { SORT_SELECT_OPTIONS } from './onGoInitialize.constants';
import type { OnGoInitializeFormValues } from './onGoInitialize.types';
import { Section } from 'api/rest/sections/sections.types';
import ScopeDropdown from './ScopeDropdown';
import { hasAtLeastOneSubsectionSelected } from './utils';

const useStyles = makeStyles(theme => ({
  intro: {
    marginBottom: theme.spacing(2.5),
  },
  introTitle: {
    fontWeight: 700,
    lineHeight: 1.2,
    marginBottom: theme.spacing(2.5),
  },
  introCopy: {
    color: theme.palette.text.secondary,
  },
  section: {
    paddingTop: theme.spacing(2.5),
    paddingBottom: theme.spacing(2.5),
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '0.75rem',
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
  },
  sectionTitleDropdown: {
    marginBottom: theme.spacing(4),
  },
  indentedElement: {
    paddingLeft: theme.spacing(3),
  },
}));

export type OnGoInitializeFormContentProps = {
  control: Control<OnGoInitializeFormValues>;
  sectionsData: Section[];
  setValue: UseFormSetValue<OnGoInitializeFormValues>;
  getValues: UseFormGetValues<OnGoInitializeFormValues>;
  trigger: UseFormTrigger<OnGoInitializeFormValues>;
  isFreePlan?: boolean;
};

const OnGoInitializeFormContent: FC<OnGoInitializeFormContentProps> = ({
  control,
  sectionsData,
  setValue,
  getValues,
  trigger,
  isFreePlan = false,
}) => {
  const classes = useStyles();
  const unansweredOnly = useWatch({ control, name: 'unansweredOnly' });
  const useAllExercises = useWatch({ control, name: 'useAllExercises' });
  const showAnswerTypes = !unansweredOnly;
  const maxQueueSize = isFreePlan ? ON_GO_ACCESS.freeMaxQueueSize : 100;
  const queueSizeCopy = isFreePlan ? ON_GO_ACCESS.copy.initialize : null;

  useEffect(() => {
    void trigger('queueSize');
  }, [useAllExercises, trigger]);

  return (
    <>
      <Box className={classes.intro}>
        <Typography className={classes.introTitle} component='h1' variant='h5'>
          Czas na szybką lekcję
        </Typography>
        <Typography className={classes.introCopy} variant='body2'>
          Wybierz zakres programu, sposób układania zadań oraz filtry. Określ, ile zadań ma znaleźć się w jednej kolejce - po zapisaniu od
          razu przejdziesz do pierwszego ćwiczenia.
        </Typography>
      </Box>

      <Box className={classes.section} component='section'>
        <Typography className={`${classes.sectionTitle} ${classes.sectionTitleDropdown}`} component='h2' id='ongo-section-scope'>
          Zakres
        </Typography>
        <Controller
          control={control}
          name='scope'
          rules={{
            validate: scope => hasAtLeastOneSubsectionSelected(scope) || 'Wybierz co najmniej jeden poddział.',
          }}
          render={renderProps => {
            return <ScopeDropdown setValue={setValue} sectionsData={sectionsData} fieldRenderProps={renderProps} />;
          }}
        />
      </Box>

      <Box className={classes.section} component='section'>
        <Typography className={`${classes.sectionTitle} ${classes.sectionTitleDropdown}`} component='h2' id='ongo-section-sort'>
          Kolejność zadań
        </Typography>
        <FormSelectInput control={control} label='Kolejność zadań' name='sort' options={SORT_SELECT_OPTIONS} />
      </Box>

      <Box className={classes.section} component='section'>
        <Typography className={classes.sectionTitle} component='h2' id='ongo-section-filters'>
          Dostępne zadania
        </Typography>
        <FormCheckboxInput control={control} label='Tylko nierozwiązane zadania' name='unansweredOnly' />
        <Collapse className={classes.indentedElement} in={showAnswerTypes}>
          <FormCheckboxInput control={control} label='Uwzględnij poprawnie rozwiązane' name='includeCorrectlyAnswered' />
          <FormCheckboxInput control={control} label='Uwzględnij błędnie rozwiązane' name='includeIncorrectlyAnswered' />
        </Collapse>
      </Box>

      <Box className={classes.section} component='section'>
        <Typography className={classes.sectionTitle} component='h2' id='ongo-section-size'>
          Wielkość kolejki
        </Typography>
        {!isFreePlan ? <FormCheckboxInput control={control} label='Użyj wszystkich zadań' name='useAllExercises' /> : null}
        <Collapse in={!useAllExercises}>
          <Controller
            control={control}
            name='queueSize'
            rules={{
              validate: v => {
                if (getValues('useAllExercises')) {
                  return true;
                }
                const n = typeof v === 'number' ? v : Number(v);
                if (!Number.isFinite(n) || n < 1) {
                  return queueSizeCopy?.queueSizeMinError ?? 'Minimum 1 zadanie';
                }
                if (!Number.isInteger(n)) {
                  return queueSizeCopy?.queueSizeIntegerError ?? 'Podaj liczbę całkowitą';
                }
                if (n > maxQueueSize) {
                  return queueSizeCopy?.queueSizeMaxError ?? `Maksimum ${maxQueueSize} zadań`;
                }
                return true;
              },
            }}
            render={({ field, fieldState }) => (
              <TextField
                fullWidth
                error={!!fieldState.error}
                helperText={fieldState.error?.message ?? queueSizeCopy?.queueSizeHelper ?? `Od 1 do ${maxQueueSize} zadań`}
                inputProps={{ min: 1, max: maxQueueSize, step: 1 }}
                label='Liczba zadań'
                margin='dense'
                size='small'
                type='number'
                variant='outlined'
                {...field}
                onChange={event => {
                  const { value } = event.target;
                  if (value === '') {
                    field.onChange('');
                    return;
                  }
                  const parsed = Number(value);
                  if (!Number.isFinite(parsed)) {
                    return;
                  }
                  field.onChange(Math.trunc(parsed));
                }}
                onKeyDown={event => {
                  if (['.', ',', 'e', 'E', '-', '+'].includes(event.key)) {
                    event.preventDefault();
                  }
                }}
                value={field.value}
              />
            )}
          />
        </Collapse>
      </Box>
    </>
  );
};

export default OnGoInitializeFormContent;
